// SuperAdmin screen - User management and database cleanup tools
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card } from '../components';
import { getUserDataForMigration, mergePlayerAccounts, mergePlayerRecords, unmergePlayer, saveAuthEmail, fixLeagueMembership, replaceLeagueMemberId } from '../services/superuserService';
import { getCompetition } from '../services/competitionService';
import { createAccountForUser, sendPasswordResetToUser } from '../services/authService';
import { linkPlayerToAuthAccount } from '../services/playerService';

const SuperAdmin = () => {
  const navigate = useNavigate();
  const { user, isSuperuser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [competitionNames, setCompetitionNames] = useState({});

  // Merge accounts state
  const [sourceUid, setSourceUid] = useState('');
  const [targetUid, setTargetUid] = useState('');
  const [merging, setMerging] = useState(false);

  // Create account state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [superuserPassword, setSuperuserPassword] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Link player to auth state
  const [linkPlayerId, setLinkPlayerId] = useState('');
  const [linkAuthUid, setLinkAuthUid] = useState('');
  const [linking, setLinking] = useState(false);

  // Merge player records state
  const [sourcePlayerId, setSourcePlayerId] = useState('');
  const [targetPlayerId, setTargetPlayerId] = useState('');
  const [mergingPlayers, setMergingPlayers] = useState(false);

  // Unmerge player state
  const [unmergePlayerId, setUnmergePlayerId] = useState('');
  const [unmerging, setUnmerging] = useState(false);

  // Register auth email state
  const [registerAuthUid, setRegisterAuthUid] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registeringEmail, setRegisteringEmail] = useState(false);

  // Fix league membership state
  const [fixLeagueId, setFixLeagueId] = useState('');
  const [fixingLeague, setFixingLeague] = useState(false);
  const [fixLeagueResult, setFixLeagueResult] = useState(null);

  // Replace league member ID state
  const [replaceLeagueId, setReplaceLeagueId] = useState('');
  const [replaceOldId, setReplaceOldId] = useState('');
  const [replaceNewId, setReplaceNewId] = useState('');
  const [replacingMemberId, setReplacingMemberId] = useState(false);

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/');
      return;
    }
    loadData();
  }, [isSuperuser, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getUserDataForMigration();
      setUserData(data);

      // Load competition names
      const compNames = {};
      for (const compId of Object.keys(data.analysis.playersByCompetition)) {
        try {
          const comp = await getCompetition(compId);
          compNames[compId] = comp?.name || 'Unknown';
        } catch {
          compNames[compId] = 'Unknown';
        }
      }
      setCompetitionNames(compNames);

    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMergeAccounts = async () => {
    if (!sourceUid || !targetUid) {
      setError('Please enter both source and target UIDs');
      return;
    }
    if (sourceUid === targetUid) {
      setError('Source and target UIDs must be different');
      return;
    }
    if (!window.confirm(`Merge all data from ${sourceUid} into ${targetUid}? This cannot be undone.`)) {
      return;
    }

    try {
      setMerging(true);
      setError('');
      const result = await mergePlayerAccounts(sourceUid, targetUid);
      setSuccess(`Merged ${result.tokensUpdated} tokens and ${result.playersUpdated} players`);
      setSourceUid('');
      setTargetUid('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error merging accounts:', err);
      setError('Failed to merge accounts: ' + err.message);
    } finally {
      setMerging(false);
    }
  };

  // Create account for user
  const handleCreateAccount = async () => {
    if (!newUserEmail || !newUserDisplayName || !superuserPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Generate a temporary password
    const tempPassword = 'Temp' + Math.random().toString(36).slice(-8) + '!';

    try {
      setCreatingAccount(true);
      setError('');
      const result = await createAccountForUser(
        newUserEmail,
        newUserDisplayName,
        tempPassword,
        user.email,
        superuserPassword
      );
      setSuccess(`Account created for ${newUserEmail}. A password reset email has been sent.`);
      setNewUserEmail('');
      setNewUserDisplayName('');
      setSuperuserPassword('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error creating account:', err);
      setError('Failed to create account: ' + err.message);
    } finally {
      setCreatingAccount(false);
    }
  };

  // Send password reset
  const handleSendPasswordReset = async () => {
    if (!resetEmail) {
      setError('Please enter an email address');
      return;
    }

    try {
      setSendingReset(true);
      setError('');
      await sendPasswordResetToUser(resetEmail);
      setSuccess(`Password reset email sent to ${resetEmail}`);
      setResetEmail('');
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError('Failed to send password reset: ' + err.message);
    } finally {
      setSendingReset(false);
    }
  };

  // Link player to auth account
  const handleLinkPlayer = async () => {
    if (!linkPlayerId || !linkAuthUid) {
      setError('Please enter both Player ID and Auth UID');
      return;
    }

    try {
      setLinking(true);
      setError('');
      await linkPlayerToAuthAccount(linkPlayerId.trim(), linkAuthUid.trim());
      setSuccess(`Player ${linkPlayerId} linked to auth account ${linkAuthUid}`);
      setLinkPlayerId('');
      setLinkAuthUid('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error linking player:', err);
      setError('Failed to link player: ' + err.message);
    } finally {
      setLinking(false);
    }
  };

  // Merge player records (same competition, same person with duplicate entries)
  const handleMergePlayerRecords = async () => {
    if (!sourcePlayerId || !targetPlayerId) {
      setError('Please enter both source and target Player IDs');
      return;
    }
    if (sourcePlayerId === targetPlayerId) {
      setError('Source and target Player IDs must be different');
      return;
    }
    if (!window.confirm(`Merge all results and data from source player into target player? The source player will be marked as merged. This cannot be undone.`)) {
      return;
    }

    try {
      setMergingPlayers(true);
      setError('');
      const result = await mergePlayerRecords(sourcePlayerId.trim(), targetPlayerId.trim());
      setSuccess(`Merged "${result.sourcePlayer}" into "${result.targetPlayer}": ${result.resultsTransferred} results, ${result.votesTransferred} votes, ${result.ratingsTransferred} ratings, ${result.achievementsTransferred} achievements, ${result.tokensTransferred} tokens transferred`);
      setSourcePlayerId('');
      setTargetPlayerId('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error merging player records:', err);
      setError('Failed to merge player records: ' + err.message);
    } finally {
      setMergingPlayers(false);
    }
  };

  // Unmerge a player (restore merged player back to active)
  const handleUnmergePlayer = async () => {
    if (!unmergePlayerId) {
      setError('Please enter a Player ID to unmerge');
      return;
    }
    if (!window.confirm(`Restore this merged player back to active status?`)) {
      return;
    }

    try {
      setUnmerging(true);
      setError('');
      const result = await unmergePlayer(unmergePlayerId.trim());
      setSuccess(`Restored "${result.displayName}" back to active status in competition ${competitionNames[result.competitionId] || result.competitionId}`);
      setUnmergePlayerId('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error unmerging player:', err);
      setError('Failed to unmerge player: ' + err.message);
    } finally {
      setUnmerging(false);
    }
  };

  // Register auth email mapping
  const handleRegisterAuthEmail = async () => {
    if (!registerAuthUid || !registerEmail) {
      setError('Please enter both Auth UID and Email');
      return;
    }

    try {
      setRegisteringEmail(true);
      setError('');
      await saveAuthEmail(registerAuthUid.trim(), registerEmail.trim());
      setSuccess(`Registered email ${registerEmail} for Auth UID ${registerAuthUid}`);
      setRegisterAuthUid('');
      setRegisterEmail('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error registering auth email:', err);
      setError('Failed to register email: ' + err.message);
    } finally {
      setRegisteringEmail(false);
    }
  };

  // Fix league membership (convert Player IDs to Auth UIDs)
  const handleFixLeagueMembership = async () => {
    if (!fixLeagueId) {
      setError('Please enter a League ID');
      return;
    }

    if (!window.confirm('This will update the league memberIds from Player IDs to Auth UIDs. Continue?')) {
      return;
    }

    try {
      setFixingLeague(true);
      setError('');
      setFixLeagueResult(null);
      const result = await fixLeagueMembership(fixLeagueId.trim());
      setFixLeagueResult(result);
      setSuccess(`Fixed league "${result.leagueName}": ${result.fixes.length} IDs updated`);
      setFixLeagueId('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error fixing league membership:', err);
      setError('Failed to fix league membership: ' + err.message);
    } finally {
      setFixingLeague(false);
    }
  };

  // Replace a specific member ID in a league
  const handleReplaceLeagueMemberId = async () => {
    if (!replaceLeagueId || !replaceOldId || !replaceNewId) {
      setError('Please fill in all fields');
      return;
    }

    if (!window.confirm(`Replace "${replaceOldId}" with "${replaceNewId}" in this league?`)) {
      return;
    }

    try {
      setReplacingMemberId(true);
      setError('');
      const result = await replaceLeagueMemberId(replaceLeagueId.trim(), replaceOldId.trim(), replaceNewId.trim());
      setSuccess(`Replaced member ID in "${result.leagueName}": ${result.oldId} → ${result.newId}`);
      setReplaceOldId('');
      setReplaceNewId('');
      await loadData(); // Refresh data
    } catch (err) {
      console.error('Error replacing league member ID:', err);
      setError('Failed to replace member ID: ' + err.message);
    } finally {
      setReplacingMemberId(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(`Copied: ${text}`);
    setTimeout(() => setSuccess(''), 2000);
  };

  if (!isSuperuser) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  const { analysis, tokens, leagues, results, gameVotes, gameRatings, achievements, games, authUsers, superusers } = userData || {};

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-red-600 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Super Admin</h1>
            <p className="text-red-100 text-sm">{user?.email}</p>
          </div>
          <Button
            variant="outline"
            className="text-white border-white hover:bg-red-700"
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        </div>
      )}
      {success && (
        <div className="max-w-7xl mx-auto px-6 mt-4">
          <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            {success}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-6">
        <div className="flex gap-2 border-b border-gray-300 overflow-x-auto">
          {['overview', 'users', 'games', 'auth', 'members', 'players', 'tokens', 'results', 'votes', 'ratings', 'achievements', 'orphaned', 'duplicates', 'leagues', 'tools'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize whitespace-nowrap ${
                activeTab === tab
                  ? 'text-red-600 border-b-2 border-red-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && analysis && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="text-center">
              <div className="text-4xl font-bold text-blue-600">{analysis.totalPlayers}</div>
              <div className="text-gray-600">Total Players</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-green-600">{analysis.playersWithOdplayerId}</div>
              <div className="text-gray-600">With Account Link</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-yellow-600">{analysis.playersWithoutOdplayerId}</div>
              <div className="text-gray-600">Without Account Link</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-purple-600">{analysis.uniqueAuthUids}</div>
              <div className="text-gray-600">Unique Auth Users</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-indigo-600">{analysis.totalTokens}</div>
              <div className="text-gray-600">Total Tokens</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-teal-600">{analysis.totalLeagues}</div>
              <div className="text-gray-600">Leagues</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-orange-600">{analysis.totalCompetitions}</div>
              <div className="text-gray-600">Competitions</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-red-600">{analysis.orphanedPlayers.length}</div>
              <div className="text-gray-600">Orphaned Players</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-pink-600">{analysis.totalResults}</div>
              <div className="text-gray-600">Game Results</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-cyan-600">{analysis.totalGameVotes}</div>
              <div className="text-gray-600">Game Votes</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-amber-600">{analysis.totalGameRatings}</div>
              <div className="text-gray-600">Game Ratings</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-lime-600">{analysis.totalAchievements}</div>
              <div className="text-gray-600">Achievements</div>
            </Card>
            <Card className="text-center">
              <div className="text-4xl font-bold text-violet-600">{analysis.totalGames}</div>
              <div className="text-gray-600">Games</div>
            </Card>
          </div>
        )}

        {/* Users Tab - Comprehensive user view */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {(() => {
              // Build comprehensive user data
              // Get all unique users by Auth UID from various sources
              const usersMap = new Map();

              // Build results count per player
              const resultsByPlayerId = new Map();
              results?.forEach(r => {
                resultsByPlayerId.set(r.playerId, (resultsByPlayerId.get(r.playerId) || 0) + 1);
              });

              // Build auth email lookup
              const authEmailLookup = new Map();
              authUsers?.forEach(u => {
                if (u.email) {
                  authEmailLookup.set(u.authUid, u.email);
                }
              });

              // Process all players to build user records
              Object.entries(analysis?.playersByCompetition || {}).forEach(([compId, compPlayers]) => {
                compPlayers.forEach(player => {
                  if (player.status === 'merged') return;

                  const authUid = player.odplayerId;
                  const resultCount = resultsByPlayerId.get(player.id) || 0;

                  if (authUid) {
                    // Player is linked to an auth account
                    if (!usersMap.has(authUid)) {
                      usersMap.set(authUid, {
                        authUid,
                        email: authEmailLookup.get(authUid) || null,
                        displayName: player.displayName,
                        players: [],
                        totalResults: 0,
                        competitions: new Set()
                      });
                    }
                    const user = usersMap.get(authUid);
                    user.players.push({
                      playerId: player.id,
                      displayName: player.displayName,
                      competitionId: compId,
                      competitionName: competitionNames[compId] || compId,
                      resultCount,
                      status: player.status
                    });
                    user.totalResults += resultCount;
                    user.competitions.add(compId);
                    // Update display name if not set
                    if (!user.displayName) {
                      user.displayName = player.displayName;
                    }
                  } else {
                    // Unlinked player - create pseudo-user entry
                    const pseudoKey = `unlinked_${player.id}`;
                    usersMap.set(pseudoKey, {
                      authUid: null,
                      email: null,
                      displayName: player.displayName,
                      players: [{
                        playerId: player.id,
                        displayName: player.displayName,
                        competitionId: compId,
                        competitionName: competitionNames[compId] || compId,
                        resultCount,
                        status: player.status
                      }],
                      totalResults: resultCount,
                      competitions: new Set([compId]),
                      isUnlinked: true
                    });
                  }
                });
              });

              // Convert to array and sort
              const allUsers = Array.from(usersMap.values())
                .map(u => ({
                  ...u,
                  competitionCount: u.competitions.size,
                  competitions: Array.from(u.competitions)
                }))
                .sort((a, b) => {
                  // Linked users first, then by result count
                  if (a.authUid && !b.authUid) return -1;
                  if (!a.authUid && b.authUid) return 1;
                  return b.totalResults - a.totalResults;
                });

              const linkedUsers = allUsers.filter(u => u.authUid);
              const unlinkedUsers = allUsers.filter(u => !u.authUid);

              return (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="text-center">
                      <div className="text-3xl font-bold text-blue-600">{linkedUsers.length}</div>
                      <div className="text-gray-600 text-sm">Linked Users</div>
                    </Card>
                    <Card className="text-center">
                      <div className="text-3xl font-bold text-yellow-600">{unlinkedUsers.length}</div>
                      <div className="text-gray-600 text-sm">Unlinked Players</div>
                    </Card>
                    <Card className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {linkedUsers.filter(u => u.email).length}
                      </div>
                      <div className="text-gray-600 text-sm">With Email</div>
                    </Card>
                    <Card className="text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {allUsers.reduce((sum, u) => sum + u.totalResults, 0)}
                      </div>
                      <div className="text-gray-600 text-sm">Total Results</div>
                    </Card>
                  </div>

                  {/* Linked Users Table */}
                  <Card>
                    <h2 className="text-xl font-bold mb-4">Linked Users ({linkedUsers.length})</h2>
                    <p className="text-sm text-gray-600 mb-4">
                      Users with Firebase Auth accounts linked to player records.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left">Display Name</th>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Auth UID</th>
                            <th className="px-3 py-2 text-center">Competitions</th>
                            <th className="px-3 py-2 text-center">Total Results</th>
                            <th className="px-3 py-2 text-left">Player Records</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linkedUsers.map(user => (
                            <tr key={user.authUid} className={`border-b hover:bg-gray-50 ${!user.email ? 'bg-yellow-50' : ''}`}>
                              <td className="px-3 py-2 font-medium">{user.displayName}</td>
                              <td className="px-3 py-2">
                                {user.email ? (
                                  <span className="text-green-600">{user.email}</span>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setRegisterAuthUid(user.authUid);
                                      setActiveTab('tools');
                                    }}
                                    className="text-yellow-600 hover:text-yellow-800 underline text-xs"
                                  >
                                    Register email
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => copyToClipboard(user.authUid)}
                                  className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all"
                                  title="Click to copy"
                                >
                                  {user.authUid}
                                </button>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {user.competitionCount}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  user.totalResults > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {user.totalResults}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="space-y-1">
                                  {user.players.map((p, idx) => (
                                    <div key={idx} className="text-xs">
                                      <span className="font-medium">{p.competitionName.split(' ')[0]}</span>
                                      <span className="text-gray-500"> ({p.resultCount})</span>
                                      <button
                                        onClick={() => copyToClipboard(p.playerId)}
                                        className="ml-2 font-mono text-gray-400 hover:text-blue-600 hover:underline"
                                        title={`Player ID: ${p.playerId} - Click to copy`}
                                      >
                                        {p.playerId.slice(0, 8)}...
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  {/* Unlinked Players Table */}
                  {unlinkedUsers.length > 0 && (
                    <Card>
                      <h2 className="text-xl font-bold mb-4 text-yellow-700">Unlinked Players ({unlinkedUsers.length})</h2>
                      <p className="text-sm text-gray-600 mb-4">
                        Players without linked auth accounts. These need to be linked to user accounts.
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-yellow-50">
                            <tr>
                              <th className="px-3 py-2 text-left">Display Name</th>
                              <th className="px-3 py-2 text-left">Competition</th>
                              <th className="px-3 py-2 text-left">Player ID</th>
                              <th className="px-3 py-2 text-center">Results</th>
                              <th className="px-3 py-2 text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unlinkedUsers.map(user => (
                              <tr key={user.players[0].playerId} className="border-b hover:bg-yellow-50 bg-yellow-25">
                                <td className="px-3 py-2 font-medium">{user.displayName}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">
                                  {user.players[0].competitionName}
                                </td>
                                <td className="px-3 py-2">
                                  <button
                                    onClick={() => copyToClipboard(user.players[0].playerId)}
                                    className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline"
                                    title="Click to copy"
                                  >
                                    {user.players[0].playerId.slice(0, 12)}...
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                                    user.totalResults > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {user.totalResults}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <button
                                    onClick={() => {
                                      setLinkPlayerId(user.players[0].playerId);
                                      setActiveTab('tools');
                                    }}
                                    className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                  >
                                    Link Account
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Games Tab */}
        {activeTab === 'games' && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="text-center">
                <div className="text-3xl font-bold text-violet-600">{games?.length || 0}</div>
                <div className="text-gray-600 text-sm">Total Games</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {games?.filter(g => g.status === 'played').length || 0}
                </div>
                <div className="text-gray-600 text-sm">Played</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {games?.filter(g => g.status === 'playing').length || 0}
                </div>
                <div className="text-gray-600 text-sm">Playing</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {games?.filter(g => g.status === 'planned').length || 0}
                </div>
                <div className="text-gray-600 text-sm">Planned</div>
              </Card>
            </div>

            {/* Games by Competition */}
            {(() => {
              // Group games by competition
              const gamesByComp = {};
              games?.forEach(game => {
                if (!gamesByComp[game.competitionId]) {
                  gamesByComp[game.competitionId] = [];
                }
                gamesByComp[game.competitionId].push(game);
              });

              // Get player count per competition
              const playerCountByComp = {};
              Object.keys(analysis?.playersByCompetition || {}).forEach(compId => {
                playerCountByComp[compId] = analysis.playersByCompetition[compId].length;
              });

              // Count results per game
              const resultsPerGame = {};
              results?.forEach(result => {
                if (!resultsPerGame[result.gameId]) {
                  resultsPerGame[result.gameId] = [];
                }
                resultsPerGame[result.gameId].push(result);
              });

              return Object.entries(gamesByComp).map(([compId, compGames]) => {
                const playerCount = playerCountByComp[compId] || 0;
                const playedGames = compGames.filter(g => g.status === 'played').length;
                const totalExpectedResults = playedGames * playerCount;
                const actualResults = compGames.reduce((sum, g) => sum + (resultsPerGame[g.id]?.length || 0), 0);
                const missingResults = totalExpectedResults - actualResults;

                return (
                  <Card key={compId}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-xl font-bold">{competitionNames[compId] || 'Unknown Competition'}</h2>
                        <p className="text-sm text-gray-500">
                          {compGames.length} games • {playerCount} players
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${missingResults > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {actualResults} / {totalExpectedResults} results
                        </div>
                        {missingResults > 0 && (
                          <p className="text-xs text-red-500">{missingResults} missing</p>
                        )}
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left">Game Name</th>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Results</th>
                            <th className="px-3 py-2 text-left">Game ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compGames
                            .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                            .map(game => {
                              const gameResults = resultsPerGame[game.id] || [];
                              const expectedResults = game.status === 'played' ? playerCount : 0;
                              const hasMissingResults = game.status === 'played' && gameResults.length < playerCount;

                              return (
                                <tr key={game.id} className={`border-b hover:bg-gray-50 ${hasMissingResults ? 'bg-red-50' : ''}`}>
                                  <td className="px-3 py-2 font-medium">{game.name}</td>
                                  <td className="px-3 py-2 text-gray-600">{game.date || '-'}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      game.status === 'played' ? 'bg-green-100 text-green-800' :
                                      game.status === 'playing' ? 'bg-blue-100 text-blue-800' :
                                      game.status === 'planned' ? 'bg-yellow-100 text-yellow-800' :
                                      game.status === 'voided' ? 'bg-gray-100 text-gray-800' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {game.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`font-medium ${hasMissingResults ? 'text-red-600' : 'text-gray-700'}`}>
                                      {gameResults.length}
                                      {game.status === 'played' && ` / ${playerCount}`}
                                    </span>
                                    {hasMissingResults && (
                                      <span className="ml-1 text-xs text-red-500">
                                        ({playerCount - gameResults.length} missing)
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    <button
                                      onClick={() => copyToClipboard(game.id)}
                                      className="text-xs font-mono text-gray-500 hover:text-blue-600 hover:underline"
                                      title="Click to copy"
                                    >
                                      {game.id.slice(0, 8)}...
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    {/* Results breakdown for this competition */}
                    <div className="mt-4 pt-4 border-t">
                      <h3 className="font-semibold text-gray-700 mb-2">Results by Player</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {(analysis?.playersByCompetition?.[compId] || []).map(player => {
                          const playerResults = results?.filter(r => r.playerId === player.id) || [];
                          const gamesPlayed = compGames.filter(g => g.status === 'played').length;
                          const participated = playerResults.filter(r => r.participated).length;

                          return (
                            <div
                              key={player.id}
                              className={`p-2 rounded text-xs ${
                                playerResults.length < gamesPlayed ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                              }`}
                            >
                              <div className="font-medium truncate" title={player.displayName}>
                                {player.displayName}
                              </div>
                              <div className={`${playerResults.length < gamesPlayed ? 'text-red-600' : 'text-gray-600'}`}>
                                {playerResults.length} / {gamesPlayed} results
                              </div>
                              <div className="text-gray-500">
                                {participated} participated
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Card>
                );
              });
            })()}
          </div>
        )}

        {/* Auth Users Tab */}
        {activeTab === 'auth' && (
          <div className="space-y-6">
            {/* Provider Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="text-center">
                <div className="text-3xl font-bold text-blue-600">{analysis?.authUsersByProvider?.total || 0}</div>
                <div className="text-gray-600 text-sm">Total Auth Users</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-red-500">{analysis?.authUsersByProvider?.google || 0}</div>
                <div className="text-gray-600 text-sm">Google Login</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-green-600">{analysis?.authUsersByProvider?.email || 0}</div>
                <div className="text-gray-600 text-sm">Email Login</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-gray-500">{analysis?.authUsersByProvider?.anonymous || 0}</div>
                <div className="text-gray-600 text-sm">Anonymous</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{analysis?.authUsersByProvider?.unknown || 0}</div>
                <div className="text-gray-600 text-sm">Unknown</div>
              </Card>
            </div>

            {/* Google Users */}
            <Card>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google Account Users ({authUsers?.filter(u => u.provider === 'google').length || 0})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Display Name</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Auth UID</th>
                      <th className="px-3 py-2 text-left">Superuser</th>
                      <th className="px-3 py-2 text-left">Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authUsers?.filter(u => u.provider === 'google').map(user => (
                      <tr key={user.authUid} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{user.displayName || '-'}</td>
                        <td className="px-3 py-2 text-blue-600">{user.email || '-'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => copyToClipboard(user.authUid)}
                            className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {user.authUid}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          {user.isSuperuser && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Superuser</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{user.sources?.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!authUsers || authUsers.filter(u => u.provider === 'google').length === 0) && (
                  <p className="text-gray-500 text-sm py-4 text-center">No Google users found</p>
                )}
              </div>
            </Card>

            {/* Email Users */}
            <Card>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="text-green-600">@</span>
                Email Account Users ({authUsers?.filter(u => u.provider === 'email').length || 0})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Display Name</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Auth UID</th>
                      <th className="px-3 py-2 text-left">Superuser</th>
                      <th className="px-3 py-2 text-left">Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authUsers?.filter(u => u.provider === 'email').map(user => (
                      <tr key={user.authUid} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{user.displayName || '-'}</td>
                        <td className="px-3 py-2 text-green-600">{user.email || '-'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => copyToClipboard(user.authUid)}
                            className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {user.authUid}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          {user.isSuperuser && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Superuser</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{user.sources?.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!authUsers || authUsers.filter(u => u.provider === 'email').length === 0) && (
                  <p className="text-gray-500 text-sm py-4 text-center">No email users found</p>
                )}
              </div>
            </Card>

            {/* Anonymous Users */}
            <Card>
              <h2 className="text-xl font-bold mb-4 text-gray-500">
                Anonymous Users ({authUsers?.filter(u => u.provider === 'anonymous').length || 0})
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                These users have only token references and no linked account. They are likely using anonymous authentication.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Auth UID</th>
                      <th className="px-3 py-2 text-left">Last Seen</th>
                      <th className="px-3 py-2 text-left">Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authUsers?.filter(u => u.provider === 'anonymous').slice(0, 50).map(user => (
                      <tr key={user.authUid} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <button
                            onClick={() => copyToClipboard(user.authUid)}
                            className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {user.authUid}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {user.lastSeenAt?.toDate ? user.lastSeenAt.toDate().toLocaleString() : '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{user.sources?.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {authUsers?.filter(u => u.provider === 'anonymous').length > 50 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Showing first 50 of {authUsers.filter(u => u.provider === 'anonymous').length} anonymous users
                  </p>
                )}
                {(!authUsers || authUsers.filter(u => u.provider === 'anonymous').length === 0) && (
                  <p className="text-gray-500 text-sm py-4 text-center">No anonymous users found</p>
                )}
              </div>
            </Card>

            {/* Unknown Provider Users */}
            {authUsers?.filter(u => u.provider === 'unknown').length > 0 && (
              <Card>
                <h2 className="text-xl font-bold mb-4 text-yellow-600">
                  Unknown Provider Users ({authUsers?.filter(u => u.provider === 'unknown').length || 0})
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  These users are in leagues or have player records but their auth provider couldn't be determined.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Display Name</th>
                        <th className="px-3 py-2 text-left">Auth UID</th>
                        <th className="px-3 py-2 text-left">Sources</th>
                      </tr>
                    </thead>
                    <tbody>
                      {authUsers?.filter(u => u.provider === 'unknown').slice(0, 50).map(user => (
                        <tr key={user.authUid} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{user.displayName || '-'}</td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => copyToClipboard(user.authUid)}
                              className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                              title="Click to copy"
                            >
                              {user.authUid}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{user.sources?.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {authUsers?.filter(u => u.provider === 'unknown').length > 50 && (
                    <p className="text-sm text-gray-500 mt-2">
                      Showing first 50 of {authUsers.filter(u => u.provider === 'unknown').length} unknown provider users
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* League Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="text-center">
                <div className="text-3xl font-bold text-teal-600">{leagues?.[0]?.members?.length || 0}</div>
                <div className="text-gray-600 text-sm">League Members</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {authUsers?.filter(u => u.provider === 'email' || u.provider === 'google').length || 0}
                </div>
                <div className="text-gray-600 text-sm">With Real Accounts</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {Object.keys(analysis?.playersByCompetition || {}).length}
                </div>
                <div className="text-gray-600 text-sm">Competitions</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {analysis?.totalPlayers || 0}
                </div>
                <div className="text-gray-600 text-sm">Total Player Records</div>
              </Card>
            </div>

            {/* League Members Overview */}
            {leagues?.map(league => {
              // Build a map of auth accounts
              const authAccountsMap = new Map();
              authUsers?.forEach(u => {
                if (u.email) {
                  authAccountsMap.set(u.authUid, u);
                }
              });

              // Build a map of player records by odplayerId
              const playersByAuthId = new Map();
              Object.entries(analysis?.playersByCompetition || {}).forEach(([compId, players]) => {
                players.forEach(player => {
                  if (player.odplayerId) {
                    if (!playersByAuthId.has(player.odplayerId)) {
                      playersByAuthId.set(player.odplayerId, []);
                    }
                    playersByAuthId.get(player.odplayerId).push({
                      ...player,
                      competitionId: compId,
                      competitionName: competitionNames[compId] || compId
                    });
                  }
                });
              });

              // Build a map of all players by displayName (normalized)
              const playersByName = new Map();
              Object.entries(analysis?.playersByCompetition || {}).forEach(([compId, players]) => {
                players.forEach(player => {
                  const normalizedName = player.displayName?.toLowerCase().trim();
                  if (normalizedName) {
                    if (!playersByName.has(normalizedName)) {
                      playersByName.set(normalizedName, []);
                    }
                    playersByName.get(normalizedName).push({
                      ...player,
                      competitionId: compId,
                      competitionName: competitionNames[compId] || compId
                    });
                  }
                });
              });

              return (
                <Card key={league.id}>
                  <h2 className="text-xl font-bold mb-4">{league.name} - Member Status</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    This shows each league member, their auth account status, and their player records across all competitions.
                    Green rows = properly linked. Yellow rows = need attention.
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left">Member Name</th>
                          <th className="px-3 py-2 text-left">Auth Account</th>
                          <th className="px-3 py-2 text-left">Auth UID</th>
                          <th className="px-3 py-2 text-left">Player Records</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {league.members?.map((member, idx) => {
                          const memberId = member.odplayerId || member.playerId;
                          const authAccount = authAccountsMap.get(memberId);
                          const linkedPlayers = playersByAuthId.get(memberId) || [];

                          // Also find players by name that might not be linked yet
                          const normalizedMemberName = member.displayName?.toLowerCase().trim();
                          const playersByThisName = playersByName.get(normalizedMemberName) || [];
                          const unlinkedPlayersByName = playersByThisName.filter(
                            p => !p.odplayerId || p.odplayerId !== memberId
                          );

                          const hasRealAccount = authAccount?.email;
                          const totalCompetitions = Object.keys(analysis?.playersByCompetition || {}).length;
                          const linkedCompetitions = linkedPlayers.length;
                          const allLinked = linkedCompetitions === totalCompetitions;
                          const hasUnlinked = unlinkedPlayersByName.length > 0;

                          let rowClass = 'bg-green-50';
                          let statusText = 'OK';
                          let statusColor = 'text-green-600';

                          if (!hasRealAccount) {
                            rowClass = 'bg-red-50';
                            statusText = 'No account';
                            statusColor = 'text-red-600';
                          } else if (hasUnlinked) {
                            rowClass = 'bg-yellow-50';
                            statusText = 'Unlinked players';
                            statusColor = 'text-yellow-600';
                          } else if (!allLinked && linkedCompetitions < totalCompetitions) {
                            rowClass = 'bg-blue-50';
                            statusText = `${linkedCompetitions}/${totalCompetitions} linked`;
                            statusColor = 'text-blue-600';
                          }

                          return (
                            <tr key={idx} className={`border-b hover:bg-gray-50 ${rowClass}`}>
                              <td className="px-3 py-2 font-medium">{member.displayName}</td>
                              <td className="px-3 py-2">
                                {authAccount?.email ? (
                                  <span className="text-green-600">{authAccount.email}</span>
                                ) : (
                                  <span className="text-red-500 text-xs">No email account</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {memberId ? (
                                  <button
                                    onClick={() => copyToClipboard(memberId)}
                                    className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline"
                                    title="Click to copy"
                                  >
                                    {memberId.slice(0, 12)}...
                                  </button>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="space-y-1">
                                  {linkedPlayers.map((player, pIdx) => (
                                    <div key={pIdx} className="flex items-center gap-2">
                                      <span className="text-xs px-1 py-0.5 bg-green-100 text-green-700 rounded">
                                        {player.competitionName}
                                      </span>
                                      <button
                                        onClick={() => copyToClipboard(player.id)}
                                        className="text-xs font-mono text-gray-500 hover:text-blue-600"
                                        title="Click to copy Player ID"
                                      >
                                        {player.id.slice(0, 8)}...
                                      </button>
                                    </div>
                                  ))}
                                  {unlinkedPlayersByName.map((player, pIdx) => (
                                    <div key={`unlinked-${pIdx}`} className="flex items-center gap-2">
                                      <span className="text-xs px-1 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                        {player.competitionName} (unlinked)
                                      </span>
                                      <button
                                        onClick={() => {
                                          setLinkPlayerId(player.id);
                                          setLinkAuthUid(memberId);
                                          setActiveTab('tools');
                                        }}
                                        className="text-xs text-purple-600 hover:text-purple-800 underline"
                                      >
                                        Link now
                                      </button>
                                    </div>
                                  ))}
                                  {linkedPlayers.length === 0 && unlinkedPlayersByName.length === 0 && (
                                    <span className="text-xs text-gray-400">No player records</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`text-xs font-medium ${statusColor}`}>{statusText}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="font-semibold text-gray-700 mb-3">Quick Actions</h3>

                    {/* Show unlinked players that need attention */}
                    {(() => {
                      const unlinkedPlayers = [];

                      Object.entries(analysis?.playersByCompetition || {}).forEach(([compId, players]) => {
                        players.forEach(player => {
                          if (player.status !== 'merged' && !player.odplayerId) {
                            // Try to find matching league member by name
                            const normalizedName = player.displayName?.toLowerCase().trim();
                            const matchingMember = league.members?.find(
                              m => m.displayName?.toLowerCase().trim() === normalizedName
                            );
                            if (matchingMember) {
                              unlinkedPlayers.push({
                                player,
                                member: matchingMember,
                                competitionId: compId,
                                competitionName: competitionNames[compId] || compId
                              });
                            }
                          }
                        });
                      });

                      if (unlinkedPlayers.length === 0) {
                        return (
                          <p className="text-sm text-green-600">All players with matching names are linked to their league member accounts.</p>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          <p className="text-sm text-yellow-600 mb-2">
                            Found {unlinkedPlayers.length} player(s) that can be linked to existing league members:
                          </p>
                          {unlinkedPlayers.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                              <span className="font-medium">{item.player.displayName}</span>
                              <span className="text-xs text-gray-500">{item.competitionName}</span>
                              <button
                                onClick={async () => {
                                  const memberId = item.member.odplayerId || item.member.playerId;
                                  if (!memberId) {
                                    setError('League member has no auth UID');
                                    return;
                                  }
                                  try {
                                    setError('');
                                    await linkPlayerToAuthAccount(item.player.id, memberId);
                                    setSuccess(`Linked ${item.player.displayName} in ${item.competitionName} to their league account`);
                                    await loadData();
                                  } catch (err) {
                                    setError('Failed to link: ' + err.message);
                                  }
                                }}
                                className="ml-auto text-xs px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                              >
                                Link to {item.member.displayName}
                              </button>
                            </div>
                          ))}

                          {/* Bulk link all button */}
                          {unlinkedPlayers.length > 1 && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Link all ${unlinkedPlayers.length} players to their matching league members?`)) {
                                  return;
                                }
                                try {
                                  setError('');
                                  let linked = 0;
                                  for (const item of unlinkedPlayers) {
                                    const memberId = item.member.odplayerId || item.member.playerId;
                                    if (memberId) {
                                      await linkPlayerToAuthAccount(item.player.id, memberId);
                                      linked++;
                                    }
                                  }
                                  setSuccess(`Successfully linked ${linked} player(s) to their league accounts`);
                                  await loadData();
                                } catch (err) {
                                  setError('Failed to link players: ' + err.message);
                                }
                              }}
                              className="mt-3 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                            >
                              Link All {unlinkedPlayers.length} Players
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </Card>
              );
            })}

            {/* Competition Player Summary */}
            <Card>
              <h2 className="text-xl font-bold mb-4">Players by Competition</h2>
              <p className="text-sm text-gray-600 mb-4">
                Shows all player records per competition and their link status.
              </p>

              {Object.entries(analysis?.playersByCompetition || {}).map(([compId, players]) => {
                const activePlayers = players.filter(p => p.status !== 'merged');
                const linkedCount = activePlayers.filter(p => p.odplayerId).length;
                const unlinkedCount = activePlayers.length - linkedCount;

                return (
                  <div key={compId} className="mb-4 p-3 bg-gray-50 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{competitionNames[compId] || compId}</h3>
                      <div className="flex gap-2 text-xs">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                          {linkedCount} linked
                        </span>
                        {unlinkedCount > 0 && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                            {unlinkedCount} unlinked
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {activePlayers.map(player => (
                        <div
                          key={player.id}
                          className={`p-2 rounded text-xs ${
                            player.odplayerId ? 'bg-green-100' : 'bg-yellow-100'
                          }`}
                        >
                          <div className="font-medium truncate">{player.displayName}</div>
                          <div className="text-gray-500">
                            {player.odplayerId ? (
                              <span className="text-green-600">Linked</span>
                            ) : (
                              <button
                                onClick={() => {
                                  setLinkPlayerId(player.id);
                                  setActiveTab('tools');
                                }}
                                className="text-purple-600 hover:underline"
                              >
                                Link account
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* Players Tab */}
        {activeTab === 'players' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">All Players by Competition</h2>
            <p className="text-sm text-gray-500 mb-4">Click any ID to copy to clipboard. Merged players are hidden.</p>
            {(() => {
              // Build a map of authUid to email for quick lookup
              const authEmailMap = new Map();
              authUsers?.forEach(u => {
                if (u.email) {
                  authEmailMap.set(u.authUid, u.email);
                }
              });

              return Object.entries(analysis?.playersByCompetition || {}).map(([compId, compPlayers]) => {
                // Filter out merged players
                const activePlayers = compPlayers.filter(p => p.status !== 'merged');
                const mergedCount = compPlayers.length - activePlayers.length;

                return (
                <div key={compId} className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="text-lg">{competitionNames[compId] || 'Unknown Competition'}</span>
                    <span className="text-sm text-gray-500">({activePlayers.length} players)</span>
                    {mergedCount > 0 && <span className="text-xs text-orange-500">({mergedCount} merged)</span>}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left">Display Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Player ID</th>
                          <th className="px-3 py-2 text-left">Auth UID (odplayerId)</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activePlayers.map(player => {
                          const playerEmail = player.odplayerId ? authEmailMap.get(player.odplayerId) : null;
                          return (
                          <tr key={player.id} className={`border-b hover:bg-gray-50 ${!player.odplayerId ? 'bg-yellow-50' : ''}`}>
                            <td className="px-3 py-2 font-medium">{player.displayName}</td>
                            <td className="px-3 py-2">
                              {playerEmail ? (
                                <span className="text-green-600 text-sm">{playerEmail}</span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => copyToClipboard(player.id)}
                                className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                                title="Click to copy"
                              >
                                {player.id}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              {player.odplayerId ? (
                                <button
                                  onClick={() => copyToClipboard(player.odplayerId)}
                                  className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline break-all text-left"
                                  title="Click to copy"
                                >
                                  {player.odplayerId}
                                </button>
                              ) : (
                                <span className="text-red-500 text-xs">Not linked</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                player.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {player.status}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              {!player.odplayerId && (
                                <button
                                  onClick={() => {
                                    setLinkPlayerId(player.id);
                                    setActiveTab('tools');
                                  }}
                                  className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                >
                                  Link Account
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
              });
            })()}
          </Card>
        )}

        {/* Tokens Tab */}
        {activeTab === 'tokens' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Player Tokens ({tokens?.length || 0})</h2>
            <p className="text-sm text-gray-500 mb-4">Click any ID to copy to clipboard</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Token ID</th>
                    <th className="px-3 py-2 text-left">Player ID</th>
                    <th className="px-3 py-2 text-left">Auth UID</th>
                    <th className="px-3 py-2 text-left">Competition</th>
                    <th className="px-3 py-2 text-left">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens?.slice(0, 100).map(token => (
                    <tr key={token.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(token.id)}
                          className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {token.id}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(token.playerId)}
                          className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {token.playerId}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(token.authUid)}
                          className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {token.authUid}
                        </button>
                      </td>
                      <td className="px-3 py-2">{competitionNames[token.competitionId] || token.competitionId}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 max-w-xs truncate">{token.deviceLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tokens?.length > 100 && (
                <p className="text-sm text-gray-500 mt-2">Showing first 100 of {tokens.length} tokens</p>
              )}
            </div>
          </Card>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Game Results ({results?.length || 0})</h2>
            <p className="text-sm text-gray-500 mb-4">Click any ID to copy to clipboard</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Result ID</th>
                    <th className="px-3 py-2 text-left">Player ID</th>
                    <th className="px-3 py-2 text-left">Game ID</th>
                    <th className="px-3 py-2 text-left">Score</th>
                    <th className="px-3 py-2 text-left">Position</th>
                    <th className="px-3 py-2 text-left">Competition</th>
                  </tr>
                </thead>
                <tbody>
                  {results?.slice(0, 100).map(result => (
                    <tr key={result.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(result.id)}
                          className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {result.id}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(result.playerId)}
                          className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {result.playerId}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(result.gameId)}
                          className="text-xs font-mono text-purple-600 hover:text-purple-800 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {result.gameId}
                        </button>
                      </td>
                      <td className="px-3 py-2">{result.score ?? '-'}</td>
                      <td className="px-3 py-2">{result.position ?? '-'}</td>
                      <td className="px-3 py-2">{competitionNames[result.competitionId] || result.competitionId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {results?.length > 100 && (
                <p className="text-sm text-gray-500 mt-2">Showing first 100 of {results.length} results</p>
              )}
            </div>
          </Card>
        )}

        {/* Votes Tab */}
        {activeTab === 'votes' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Game Votes ({gameVotes?.length || 0})</h2>
            <p className="text-sm text-gray-500 mb-4">Click any ID to copy to clipboard</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Vote ID</th>
                    <th className="px-3 py-2 text-left">Player ID (Voter)</th>
                    <th className="px-3 py-2 text-left">Game Section ID</th>
                    <th className="px-3 py-2 text-left">League ID</th>
                    <th className="px-3 py-2 text-left">Votes</th>
                  </tr>
                </thead>
                <tbody>
                  {gameVotes?.slice(0, 100).map(vote => (
                    <tr key={vote.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(vote.id)}
                          className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {vote.id}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(vote.playerId)}
                          className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {vote.playerId}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        {vote.gameSectionId && (
                          <button
                            onClick={() => copyToClipboard(vote.gameSectionId)}
                            className="text-xs font-mono text-purple-600 hover:text-purple-800 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {vote.gameSectionId}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {vote.leagueId && (
                          <button
                            onClick={() => copyToClipboard(vote.leagueId)}
                            className="text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {vote.leagueId}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {vote.votes?.length || 0} votes
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {gameVotes?.length > 100 && (
                <p className="text-sm text-gray-500 mt-2">Showing first 100 of {gameVotes.length} vote records</p>
              )}
            </div>
          </Card>
        )}

        {/* Ratings Tab */}
        {activeTab === 'ratings' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Game Ratings ({gameRatings?.length || 0})</h2>
            <p className="text-sm text-gray-500 mb-4">Click any ID to copy to clipboard</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Rating ID</th>
                    <th className="px-3 py-2 text-left">Player ID</th>
                    <th className="px-3 py-2 text-left">Game ID</th>
                    <th className="px-3 py-2 text-left">Rating</th>
                    <th className="px-3 py-2 text-left">Competition</th>
                  </tr>
                </thead>
                <tbody>
                  {gameRatings?.slice(0, 100).map(rating => (
                    <tr key={rating.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(rating.id)}
                          className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {rating.id}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(rating.playerId)}
                          className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {rating.playerId}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        {rating.gameId && (
                          <button
                            onClick={() => copyToClipboard(rating.gameId)}
                            className="text-xs font-mono text-purple-600 hover:text-purple-800 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {rating.gameId}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs">
                          {rating.rating ?? '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2">{competitionNames[rating.competitionId] || rating.competitionId || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {gameRatings?.length > 100 && (
                <p className="text-sm text-gray-500 mt-2">Showing first 100 of {gameRatings.length} ratings</p>
              )}
            </div>
          </Card>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Achievements ({achievements?.length || 0})</h2>
            <p className="text-sm text-gray-500 mb-4">Click any ID to copy to clipboard</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Achievement ID</th>
                    <th className="px-3 py-2 text-left">Player ID</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Competition</th>
                  </tr>
                </thead>
                <tbody>
                  {achievements?.slice(0, 100).map(achievement => (
                    <tr key={achievement.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(achievement.id)}
                          className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {achievement.id}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyToClipboard(achievement.playerId)}
                          className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline break-all text-left"
                          title="Click to copy"
                        >
                          {achievement.playerId}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 bg-lime-100 text-lime-800 rounded text-xs">
                          {achievement.type || achievement.achievementType || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2">{competitionNames[achievement.competitionId] || achievement.competitionId || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {achievements?.length > 100 && (
                <p className="text-sm text-gray-500 mt-2">Showing first 100 of {achievements.length} achievements</p>
              )}
            </div>
          </Card>
        )}

        {/* Orphaned Players Tab */}
        {activeTab === 'orphaned' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Orphaned Players ({analysis?.orphanedPlayers?.length || 0})</h2>
            <p className="text-gray-600 mb-4">
              These players have no tokens and no odplayerId. They may be old accounts that were never properly linked.
            </p>
            {analysis?.orphanedPlayers?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Display Name</th>
                      <th className="px-3 py-2 text-left">Player ID</th>
                      <th className="px-3 py-2 text-left">Competition</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.orphanedPlayers.map(player => (
                      <tr key={player.id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{player.displayName}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => copyToClipboard(player.id)}
                            className="text-xs font-mono text-gray-600 hover:text-blue-600 hover:underline break-all text-left"
                            title="Click to copy"
                          >
                            {player.id}
                          </button>
                        </td>
                        <td className="px-3 py-2">{competitionNames[player.competitionId] || player.competitionId}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            player.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {player.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-green-600">No orphaned players found.</p>
            )}
          </Card>
        )}

        {/* Duplicates Tab */}
        {activeTab === 'duplicates' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Duplicate Display Names</h2>
            <p className="text-gray-600 mb-4">
              These display names appear in multiple competitions. Some may be the same person.
            </p>
            {Object.keys(analysis?.duplicateNames || {}).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(analysis.duplicateNames).map(([name, occurrences]) => (
                  <div key={name} className="border rounded p-3">
                    <h3 className="font-semibold text-gray-900 mb-2">"{name}" ({occurrences.length} occurrences)</h3>
                    <div className="space-y-2">
                      {occurrences.map((occ, idx) => (
                        <div key={idx} className="text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 p-2 bg-gray-50 rounded">
                          <span className="text-gray-700 font-medium">{competitionNames[occ.competitionId] || occ.competitionId}</span>
                          <button
                            onClick={() => copyToClipboard(occ.playerId)}
                            className="text-xs font-mono text-gray-500 hover:text-blue-600 hover:underline"
                            title="Click to copy Player ID"
                          >
                            player: {occ.playerId}
                          </button>
                          {occ.odplayerId ? (
                            <button
                              onClick={() => copyToClipboard(occ.odplayerId)}
                              className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline"
                              title="Click to copy Auth UID"
                            >
                              auth: {occ.odplayerId}
                            </button>
                          ) : (
                            <span className="text-xs text-red-500">no auth</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-green-600">No duplicate display names found.</p>
            )}
          </Card>
        )}

        {/* Leagues Tab */}
        {activeTab === 'leagues' && (
          <Card>
            <h2 className="text-xl font-bold mb-4">Leagues ({leagues?.length || 0})</h2>
            {leagues?.map(league => (
              <div key={league.id} className="mb-6 border-b pb-4">
                <h3 className="font-semibold text-gray-900 text-lg">{league.name}</h3>
                <div className="text-sm text-gray-500 mb-2">
                  <span>Admin: {league.adminName} </span>
                  <button
                    onClick={() => copyToClipboard(league.adminId)}
                    className="font-mono text-xs text-gray-400 hover:text-blue-600 hover:underline"
                    title="Click to copy Admin ID"
                  >
                    ({league.adminId})
                  </button>
                </div>
                <div className="text-sm">
                  <span className="font-medium">Members ({league.members?.length || 0}):</span>
                  <div className="mt-2 space-y-1">
                    {league.members?.map((member, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 p-2 rounded ${
                          member.role === 'admin' ? 'bg-red-50' : 'bg-gray-50'
                        }`}
                      >
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          member.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {member.role}
                        </span>
                        <span className="font-medium">{member.displayName}</span>
                        {(member.odplayerId || member.playerId) && (
                          <button
                            onClick={() => copyToClipboard(member.odplayerId || member.playerId)}
                            className="text-xs font-mono text-green-600 hover:text-green-800 hover:underline"
                            title="Click to copy"
                          >
                            {member.odplayerId || member.playerId}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            <Card>
              <h2 className="text-xl font-bold mb-4">Merge User Accounts</h2>
              <p className="text-gray-600 mb-4">
                Merge all player tokens and odplayerId references from one auth UID to another.
                Use this when the same person has multiple Firebase accounts.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Auth UID (will be merged FROM)
                  </label>
                  <input
                    type="text"
                    value={sourceUid}
                    onChange={(e) => setSourceUid(e.target.value.trim())}
                    placeholder="Enter source auth UID to merge from"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Auth UID (will be merged INTO)
                  </label>
                  <input
                    type="text"
                    value={targetUid}
                    onChange={(e) => setTargetUid(e.target.value.trim())}
                    placeholder="Enter target auth UID to merge into"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <Button
                  onClick={handleMergeAccounts}
                  disabled={merging || !sourceUid || !targetUid}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {merging ? 'Merging...' : 'Merge Accounts'}
                </Button>
              </div>
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Merge Player Records</h2>
              <p className="text-gray-600 mb-4">
                Merge two player records in the SAME competition (when same person has duplicate entries).
                Transfers all results, votes, ratings, achievements, and tokens from source to target.
                The source player will be marked as "merged" and kept for audit trail.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Player ID (will be merged FROM - this player's data moves to target)
                  </label>
                  <input
                    type="text"
                    value={sourcePlayerId}
                    onChange={(e) => setSourcePlayerId(e.target.value.trim())}
                    placeholder="e.g., ebae780e-ec44-465f-bbc6-a544e929abe0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Player ID (will be merged INTO - this player keeps all the data)
                  </label>
                  <input
                    type="text"
                    value={targetPlayerId}
                    onChange={(e) => setTargetPlayerId(e.target.value.trim())}
                    placeholder="e.g., 00a487df-e43a-4676-86a9-8dcfb85f2ce0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <Button
                  onClick={handleMergePlayerRecords}
                  disabled={mergingPlayers || !sourcePlayerId || !targetPlayerId}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {mergingPlayers ? 'Merging Player Records...' : 'Merge Player Records'}
                </Button>
              </div>

              {/* Quick reference: show duplicate names */}
              {analysis?.duplicateNames && Object.keys(analysis.duplicateNames).length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="font-semibold text-gray-700 mb-2">Players with Same Name (potential duplicates)</h3>
                  <div className="space-y-2 text-sm">
                    {Object.entries(analysis.duplicateNames).map(([name, players]) => (
                      <div key={name} className="p-2 bg-yellow-50 rounded border border-yellow-200">
                        <div className="font-medium text-yellow-800 capitalize">{name}</div>
                        <div className="space-y-1 mt-1">
                          {players.map((p, idx) => (
                            <div key={p.playerId} className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500">Competition: {competitionNames[p.competitionId] || p.competitionId}</span>
                              <button
                                onClick={() => idx === 0 ? setSourcePlayerId(p.playerId) : setTargetPlayerId(p.playerId)}
                                className={`font-mono ${idx === 0 ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} hover:underline`}
                                title={idx === 0 ? 'Click to set as Source' : 'Click to set as Target'}
                              >
                                {p.playerId}
                              </button>
                              {p.odplayerId && <span className="text-purple-600">(linked)</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Unmerge Player</h2>
              <p className="text-gray-600 mb-4">
                Restore a merged player back to active status. Use this if you accidentally merged wrong players.
                Note: This only restores the player status - it does NOT move results back automatically.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player ID to Unmerge
                  </label>
                  <input
                    type="text"
                    value={unmergePlayerId}
                    onChange={(e) => setUnmergePlayerId(e.target.value.trim())}
                    placeholder="e.g., ebae780e-ec44-465f-bbc6-a544e929abe0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <Button
                  onClick={handleUnmergePlayer}
                  disabled={unmerging || !unmergePlayerId}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {unmerging ? 'Restoring...' : 'Restore Player to Active'}
                </Button>
              </div>

              {/* Show merged players for quick access */}
              {(() => {
                const mergedPlayers = Object.values(analysis?.playersByCompetition || {})
                  .flat()
                  .filter(p => p.status === 'merged');

                if (mergedPlayers.length === 0) return null;

                return (
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="font-semibold text-gray-700 mb-2">Merged Players (click to select)</h3>
                    <div className="space-y-1 text-sm">
                      {mergedPlayers.map(p => (
                        <div key={p.id} className="flex items-center gap-2 p-2 bg-orange-50 rounded border border-orange-200">
                          <span className="font-medium">{p.displayName}</span>
                          <span className="text-gray-500 text-xs">
                            {competitionNames[p.competitionId] || p.competitionId}
                          </span>
                          <button
                            onClick={() => setUnmergePlayerId(p.id)}
                            className="ml-auto text-xs font-mono text-teal-600 hover:text-teal-800 hover:underline"
                          >
                            {p.id}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Link Player to Auth Account</h2>
              <p className="text-gray-600 mb-4">
                Link an existing player record to an auth account by setting the odplayerId field.
                This connects their game results to their login account.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player ID (from Players tab)
                  </label>
                  <input
                    type="text"
                    value={linkPlayerId}
                    onChange={(e) => setLinkPlayerId(e.target.value)}
                    placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auth UID (Firebase Auth User ID)
                  </label>
                  <input
                    type="text"
                    value={linkAuthUid}
                    onChange={(e) => setLinkAuthUid(e.target.value)}
                    placeholder="e.g., kpP1TX4nddNbHHlX74sikmKhTzt2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <Button
                  onClick={handleLinkPlayer}
                  disabled={linking || !linkPlayerId || !linkAuthUid}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {linking ? 'Linking...' : 'Link Player to Account'}
                </Button>
              </div>

              {/* Quick reference of known auth users */}
              <div className="mt-6 pt-4 border-t">
                <h3 className="font-semibold text-gray-700 mb-2">Known Auth Users (click to use)</h3>
                <div className="space-y-1 text-sm">
                  {authUsers?.filter(u => u.email).map(u => (
                    <div key={u.authUid} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <span className="font-medium">{u.displayName || u.email}</span>
                      <span className="text-gray-500">{u.email}</span>
                      <button
                        onClick={() => setLinkAuthUid(u.authUid)}
                        className="ml-auto text-xs font-mono text-purple-600 hover:text-purple-800 hover:underline"
                      >
                        {u.authUid}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Register Auth Email</h2>
              <p className="text-gray-600 mb-4">
                Register an email address for an Auth UID. This is needed because Firebase Auth emails can't be queried directly from the client.
                Copy the Auth UID and Email from Firebase Console → Authentication → Users.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auth UID (from Firebase Console)
                  </label>
                  <input
                    type="text"
                    value={registerAuthUid}
                    onChange={(e) => setRegisterAuthUid(e.target.value)}
                    placeholder="e.g., 8bebr9yZ27UDT1DLxeSoxSNAD4J"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    placeholder="e.g., user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <Button
                  onClick={handleRegisterAuthEmail}
                  disabled={registeringEmail || !registerAuthUid || !registerEmail}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {registeringEmail ? 'Registering...' : 'Register Email'}
                </Button>
              </div>

              {/* Show players without email for quick access */}
              {(() => {
                const playersWithoutEmail = [];
                Object.entries(analysis?.playersByCompetition || {}).forEach(([compId, players]) => {
                  players.forEach(player => {
                    if (player.odplayerId && player.status !== 'merged') {
                      const authUser = authUsers?.find(u => u.authUid === player.odplayerId);
                      if (!authUser?.email) {
                        playersWithoutEmail.push({
                          ...player,
                          competitionName: competitionNames[compId] || compId
                        });
                      }
                    }
                  });
                });

                if (playersWithoutEmail.length === 0) return null;

                return (
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="font-semibold text-gray-700 mb-2">Players Missing Email (click to use Auth UID)</h3>
                    <div className="space-y-1 text-sm">
                      {playersWithoutEmail.map(p => (
                        <div key={p.id} className="flex items-center gap-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                          <span className="font-medium">{p.displayName}</span>
                          <span className="text-xs text-gray-500">{p.competitionName}</span>
                          <button
                            onClick={() => setRegisterAuthUid(p.odplayerId)}
                            className="ml-auto text-xs font-mono text-cyan-600 hover:text-cyan-800 hover:underline"
                          >
                            {p.odplayerId}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Fix League Membership</h2>
              <p className="text-gray-600 mb-4">
                Update a league's memberIds from Player IDs (UUIDs) to Auth UIDs.
                This fixes the issue where logged-in users can't see their leagues because the query uses Auth UID but the league stores Player IDs.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    League ID
                  </label>
                  <input
                    type="text"
                    value={fixLeagueId}
                    onChange={(e) => setFixLeagueId(e.target.value)}
                    placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <Button
                  onClick={handleFixLeagueMembership}
                  disabled={fixingLeague || !fixLeagueId}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {fixingLeague ? 'Fixing...' : 'Fix League Membership'}
                </Button>
              </div>

              {/* Show fix result */}
              {fixLeagueResult && (
                <div className="mt-4 p-4 bg-green-50 rounded border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2">Fixed: {fixLeagueResult.leagueName}</h3>
                  <div className="text-sm text-green-800">
                    <p><strong>Fixes applied:</strong> {fixLeagueResult.fixes.length}</p>
                    {fixLeagueResult.fixes.map((fix, idx) => (
                      <div key={idx} className="mt-1 ml-4 text-xs font-mono">
                        {fix.displayName}: {fix.oldId.substring(0, 12)}... → {fix.newId.substring(0, 12)}...
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show leagues for quick access */}
              {leagues && leagues.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="font-semibold text-gray-700 mb-2">Available Leagues (click to select)</h3>
                  <div className="space-y-1 text-sm">
                    {leagues.map(league => (
                      <div key={league.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                        <span className="font-medium">{league.name}</span>
                        <span className="text-xs text-gray-500">({league.memberIds?.length || 0} members)</span>
                        <button
                          onClick={() => setFixLeagueId(league.id)}
                          className="ml-auto text-xs font-mono text-indigo-600 hover:text-indigo-800 hover:underline"
                        >
                          {league.id}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Replace League Member ID</h2>
              <p className="text-gray-600 mb-4">
                Directly replace one Auth UID with another in a league's memberIds.
                Use this when an Auth UID was entered incorrectly (e.g., missing characters).
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    League ID
                  </label>
                  <input
                    type="text"
                    value={replaceLeagueId}
                    onChange={(e) => setReplaceLeagueId(e.target.value)}
                    placeholder="Select from list below or paste league ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Old Auth UID (to replace)
                  </label>
                  <input
                    type="text"
                    value={replaceOldId}
                    onChange={(e) => setReplaceOldId(e.target.value)}
                    placeholder="e.g., 8bebr9yZ27UDT1DLxeSoxSNAD4J"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Auth UID (correct value)
                  </label>
                  <input
                    type="text"
                    value={replaceNewId}
                    onChange={(e) => setReplaceNewId(e.target.value)}
                    placeholder="e.g., 8bebr9yZ27UDT1DLxeSoxSNAD4J2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <Button
                  onClick={handleReplaceLeagueMemberId}
                  disabled={replacingMemberId || !replaceLeagueId || !replaceOldId || !replaceNewId}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {replacingMemberId ? 'Replacing...' : 'Replace Member ID'}
                </Button>
              </div>

              {/* Show leagues and their members for quick access */}
              {leagues && leagues.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="font-semibold text-gray-700 mb-2">League Members (click to use)</h3>
                  <div className="space-y-3 text-sm">
                    {leagues.map(league => (
                      <div key={league.id} className="p-2 bg-gray-50 rounded border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">{league.name}</span>
                          <button
                            onClick={() => setReplaceLeagueId(league.id)}
                            className="text-xs text-rose-600 hover:text-rose-800 underline"
                          >
                            Select
                          </button>
                        </div>
                        <div className="space-y-1 ml-2">
                          {league.members?.map((member, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <span>{member.displayName}:</span>
                              <button
                                onClick={() => {
                                  setReplaceLeagueId(league.id);
                                  setReplaceOldId(member.playerId || member.odplayerId);
                                }}
                                className="font-mono text-gray-600 hover:text-rose-600 hover:underline"
                              >
                                {member.playerId || member.odplayerId}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Create User Account</h2>
              <p className="text-gray-600 mb-4">
                Create an email account for a user. They will receive a password reset email to set their own password.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User's Email
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value.trim())}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={newUserDisplayName}
                    onChange={(e) => setNewUserDisplayName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Password (to re-authenticate after)
                  </label>
                  <input
                    type="password"
                    value={superuserPassword}
                    onChange={(e) => setSuperuserPassword(e.target.value)}
                    placeholder="Your superuser password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required because creating an account temporarily signs you out
                  </p>
                </div>
                <Button
                  onClick={handleCreateAccount}
                  disabled={creatingAccount || !newUserEmail || !newUserDisplayName || !superuserPassword}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {creatingAccount ? 'Creating...' : 'Create Account & Send Reset Email'}
                </Button>
              </div>
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">Send Password Reset</h2>
              <p className="text-gray-600 mb-4">
                Send a password reset email to any user so they can set/change their password.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User's Email
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value.trim())}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <Button
                  onClick={handleSendPasswordReset}
                  disabled={sendingReset || !resetEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingReset ? 'Sending...' : 'Send Password Reset Email'}
                </Button>
              </div>
            </Card>

            <Card>
              <h2 className="text-xl font-bold mb-4">How to Use These Tools</h2>
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <h3 className="font-semibold text-gray-900">Creating User Accounts</h3>
                  <p>Use the "Create User Account" tool to set up email accounts for your players. They'll receive an email to set their password.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Merging Duplicate Accounts</h3>
                  <p>If someone has multiple accounts, go to the "Duplicates" tab to find them, then use "Merge User Accounts" to combine them.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Password Resets</h3>
                  <p>If a user forgot their password, use "Send Password Reset" to let them reset it themselves.</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Refresh Button */}
        <div className="mt-6">
          <Button onClick={loadData}>
            Refresh Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdmin;
